'use client'
import * as React from 'react'
import * as LabelPrimitive from '@radix-ui/react-label'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'

export type InputProps = React.InputHTMLAttributes<HTMLInputElement> & {
  /**
   * Transaction name for RUM monitoring.
   * Required for observability - tracks user interactions in Elastic APM.
   * Format: area.page.field_name (e.g., "admin.login.username")
   */
  transactionName: string
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, transactionName, value, ...props }, ref) => {
    return (
      <input
        placeholder="" // Label Location is Set to Placeholder hence it is needed
        type={type}
        data-transaction-name={transactionName}
        value={value ?? ''}
        className={cn(
          'text-primary ring-purple-50 transition ease-out',
          'focus-visible:border-transparent focus-visible:ring-2 focus-visible:ring-ring',
          'border-gray-300 disabled:cursor-not-allowed', // Disabled Styles
          'read-only:cursor-not-allowed read-only:opacity-50', // Read only Styles
          'file:border-0 file:bg-transparent file:text-sm file:font-medium', // File Styles
          'h-[3.75rem] w-full rounded-xl border p-4 outline-none focus-visible:shadow-fieldshadow md:w-[21.8125rem]',
          'peer',
          className
        )}
        ref={ref}
        {...props}
      />
    )
  }
)
Input.displayName = 'Input'

export { Input }

/*
 * TO-DO: Updating Label with relative and this stylew was a mistake and needs to be fixed
 */
const labelVariants = cva(
  'origin-top-left bg-white  font-medium leading-none text-muted-foreground transition-all duration-300 ease-out  peer-read-only:cursor-not-allowed peer-read-only:text-muted-foreground peer-focus:scale-90 peer-focus:text-sm peer-focus:text-ring peer-disabled:cursor-not-allowed'
)

const Label = React.forwardRef<
  React.ComponentRef<typeof LabelPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof LabelPrimitive.Root> &
    VariantProps<typeof labelVariants>
>(({ className, ...props }, ref) => (
  <LabelPrimitive.Root
    ref={ref}
    className={cn(labelVariants(), className)}
    {...props}
  />
))
Label.displayName = LabelPrimitive.Root.displayName

export { Label }
